import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV } from "@/constants/testIds";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_phenix-patrimoine/artifacts/dsl8an5f_CA44C4B2-B46B-4B33-A16B-73FC73E4AFB6.PNG";

const links = [
  { id: NAV.linkExpertise, label: "Expertise", hash: "expertise" },
  { id: NAV.linkEntreprises, label: "Entreprises", hash: "cibles" },
  { id: NAV.linkParticuliers, label: "Particuliers", hash: "cibles" },
  { id: NAV.linkApproche, label: "Notre approche", hash: "approche" },
  { id: NAV.linkActualites, label: "Actualités", route: "/actualites" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (hash) => {
    setOpen(false);
    if (onHome) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${hash}`);
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 200);
    }
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || !onHome
          ? "bg-[#f7f5f0]/85 backdrop-blur-xl border-b border-[#0a192f]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid={NAV.logo}
          className="flex items-center gap-3 group"
        >
          <span className="w-11 h-11 flex items-center justify-center bg-[#0a192f] overflow-hidden transition-transform duration-500 group-hover:scale-105">
            <img
              src={LOGO_URL}
              alt="Phénix Patrimoine & Assurance"
              className="w-full h-full object-contain p-1"
            />
          </span>
          <div className="leading-tight">
            <div className="font-serif text-[19px] tracking-tight text-[#0a192f]">
              Phénix
            </div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#475569] font-medium">
              Patrimoine · Assurance
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) =>
            l.route ? (
              <Link
                key={l.id}
                data-testid={l.id}
                to={l.route}
                className="text-sm text-[#0a192f]/80 hover:text-[#c05c3d] transition-colors duration-300 tracking-wide"
              >
                {l.label}
              </Link>
            ) : (
              <button
                key={l.id}
                data-testid={l.id}
                onClick={() => goToSection(l.hash)}
                className="text-sm text-[#0a192f]/80 hover:text-[#c05c3d] transition-colors duration-300 tracking-wide"
              >
                {l.label}
              </button>
            )
          )}
        </nav>

        <div className="hidden lg:block">
          <button
            data-testid={NAV.ctaContact}
            onClick={() => goToSection("contact")}
            className="inline-flex items-center gap-2 bg-[#0a192f] text-[#efeade] px-6 py-3 text-sm tracking-wide hover:bg-[#c05c3d] transition-colors duration-300"
          >
            Prendre rendez-vous
            <span aria-hidden>→</span>
          </button>
        </div>

        <button
          data-testid={NAV.mobileToggle}
          onClick={() => setOpen((s) => !s)}
          className="lg:hidden text-[#0a192f] p-2"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid={NAV.mobileMenu}
          className="lg:hidden bg-[#f7f5f0] border-t border-[#0a192f]/10"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) =>
              l.route ? (
                <Link
                  key={l.id}
                  to={l.route}
                  onClick={() => setOpen(false)}
                  className="text-lg text-[#0a192f] font-serif"
                >
                  {l.label}
                </Link>
              ) : (
                <button
                  key={l.id}
                  onClick={() => goToSection(l.hash)}
                  className="text-left text-lg text-[#0a192f] font-serif"
                >
                  {l.label}
                </button>
              )
            )}
            <button
              onClick={() => goToSection("contact")}
              className="mt-2 inline-flex items-center justify-center bg-[#0a192f] text-[#efeade] px-6 py-3 text-sm"
            >
              Prendre rendez-vous →
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
